import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { TokenErrorCode, TokenErrorResponse } from '../interfaces/token.interface';
import { AuthCleanupService } from '../services/auth-cleanup.service';
import { AuthStateService } from '../services/auth-state.service';
import { RefreshTokenService } from '../services/refresh-token.service';
import { TokenService } from '../services/token.service';

interface RawTokenErrorPayload {
  statusCode?: number | string;
  status?: number | string;
  error?: string;
  message?: string | string[];
  code?: TokenErrorCode | string;
  expiredAt?: string;
}

interface TokenErrorEnvelope {
  error?: RawTokenErrorPayload;
}

export const smartAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const refreshTokenService = inject(RefreshTokenService);
  const authStateService = inject(AuthStateService);
  const authCleanupService = inject(AuthCleanupService);
  const router = inject(Router);

  // Add token if exists - API will be responsible for validating expiration and signature
  const token = tokenService.getAccessToken();
  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 errors with specific token codes
      if (error.status === 401) {
        return handleTokenError(
          error,
          req,
          next,
          tokenService,
          refreshTokenService,
          authCleanupService,
          router
        );
      }
      return throwError(() => error);
    })
  );
};

/**
 * Handles specific token errors according to error codes
 * The server is the single source of truth for token validation
 */
function handleTokenError(
  error: HttpErrorResponse,
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  tokenService: TokenService,
  refreshTokenService: RefreshTokenService,
  authCleanupService: AuthCleanupService,
  router: Router
): Observable<HttpEvent<any>> {
  const tokenError = extractTokenError(error);

  if (!tokenError) {
    // If not a specific token error, try refresh if possible
    if (shouldAttemptRefresh(req, tokenService)) {
      return handleGenericTokenRefresh(
        req,
        next,
        tokenService,
        refreshTokenService,
        authCleanupService,
        router
      );
    }
    // If we can't refresh and it's a 401, create a generic error and redirect
    const genericError: TokenErrorResponse = {
      statusCode: 401,
      error: 'Authentication Error',
      message: error.error?.message || error.message || 'Unauthorized',
      code: TokenErrorCode.AUTH_FAILED,
    };
    redirectToLogin(genericError, authCleanupService, router);
    return throwError(() => error);
  }

  // Handle according to error type
  switch (tokenError.code) {
    case TokenErrorCode.TOKEN_EXPIRED:
      return handleTokenExpired(
        tokenError,
        req,
        next,
        tokenService,
        refreshTokenService,
        authCleanupService,
        router
      );

    case TokenErrorCode.TOKEN_MALFORMED:
    case TokenErrorCode.TOKEN_INVALID_SIGNATURE:
    case TokenErrorCode.TOKEN_INVALID_ALGORITHM:
    case TokenErrorCode.TOKEN_INVALID:
    case TokenErrorCode.NO_TOKEN:
    case TokenErrorCode.AUTH_FAILED:
    case TokenErrorCode.TOO_MANY_REQUESTS:
      return handleTokenInvalid(tokenError, authCleanupService, router);

    default:
      return handleGenericTokenError(tokenError, authCleanupService, router);
  }
}

/**
 * Normalizes statusCode to a number, handling both string and number inputs
 */
function normalizeStatusCode(
  statusCode?: number | string,
  status?: number | string,
  fallbackStatus?: number
): number {
  // Try statusCode first
  if (statusCode !== undefined && statusCode !== null) {
    const num = typeof statusCode === 'string' ? parseInt(statusCode, 10) : statusCode;
    if (!isNaN(num)) {
      return num;
    }
  }

  // Try status if it's a number
  if (status !== undefined && status !== null && typeof status === 'number') {
    return status;
  }

  // Fallback to error.status from HttpErrorResponse
  return fallbackStatus || 401;
}

/**
 * Extracts token error information from HTTP response
 */
function extractTokenError(error: HttpErrorResponse): TokenErrorResponse | null {
  try {
    const errorBody = error.error as
      | TokenErrorEnvelope
      | RawTokenErrorPayload
      | string
      | null
      | undefined;

    // Support nested error structure like: { error: { code, message, status } }
    let candidate: RawTokenErrorPayload | undefined;

    if (errorBody && typeof errorBody === 'object') {
      const envelope = errorBody as TokenErrorEnvelope;
      if (envelope.error && typeof envelope.error === 'object') {
        candidate = envelope.error;
      } else {
        candidate = errorBody as RawTokenErrorPayload;
      }
    }

    // If we have a candidate object with a code, build the TokenErrorResponse
    if (candidate && candidate.code) {
      // Normalize statusCode: convert string to number, or use status if it's a number, or fallback to error.status
      const normalizedStatusCode = normalizeStatusCode(
        candidate.statusCode,
        candidate.status,
        error.status
      );

      const tokenError: TokenErrorResponse = {
        statusCode: normalizedStatusCode,
        error: candidate.error || 'Authentication Error',
        message: Array.isArray(candidate.message)
          ? candidate.message.join(', ')
          : candidate.message || 'An authentication error occurred',
        code: candidate.code as TokenErrorCode,
        expiredAt: candidate.expiredAt,
      };
      return tokenError;
    }

    // If errorBody is a string, try to parse it
    if (typeof errorBody === 'string') {
      try {
        const parsed = JSON.parse(errorBody) as RawTokenErrorPayload | null;
        if (parsed && parsed.code) {
          const normalizedMessage = Array.isArray(parsed.message)
            ? parsed.message.join(', ')
            : parsed.message || 'An authentication error occurred';

          // Normalize statusCode: convert string to number, or use status if it's a number, or fallback to error.status
          const normalizedStatusCode = normalizeStatusCode(
            parsed.statusCode,
            parsed.status,
            error.status
          );

          const tokenError: TokenErrorResponse = {
            statusCode: normalizedStatusCode,
            error: parsed.error || 'Authentication Error',
            message: normalizedMessage,
            code: parsed.code as TokenErrorCode,
            expiredAt: parsed.expiredAt,
          };
          return tokenError;
        }
      } catch (parseError) {}
    }
  } catch (e) {}
  return null;
}

/**
 * Maneja token expirado - intenta refresh token
 */
function handleTokenExpired(
  tokenError: TokenErrorResponse,
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  tokenService: TokenService,
  refreshTokenService: RefreshTokenService,
  authCleanupService: AuthCleanupService,
  router: Router
): Observable<HttpEvent<any>> {
  if (!shouldAttemptRefresh(req, tokenService)) {
    redirectToLogin(tokenError, authCleanupService, router);
    return throwError(() => new Error('Token expired and cannot refresh'));
  }

  // Capturar el RT antes de iniciar el refresh.
  // Usado en el catchError para detectar si otra pestaña ya refrescó con éxito.
  const sentRefreshToken = tokenService.getRefreshToken();

  return refreshTokenService.refreshToken().pipe(
    switchMap(() => retryRequest(req, next, tokenService)),
    catchError((refreshError) => {
      // Staleness Check: si el RT en localStorage cambió mientras esta pestaña
      // fallaba, significa que otra pestaña ganó la carrera y ya guardó tokens
      // válidos. Reutilizarlos en lugar de forzar el logout.
      const currentRefreshToken = tokenService.getRefreshToken();
      if (currentRefreshToken && currentRefreshToken !== sentRefreshToken) {
        return retryRequest(req, next, tokenService);
      }
      redirectToLogin(tokenError, authCleanupService, router);
      return throwError(() => refreshError);
    })
  );
}

/**
 * Handles invalid tokens - redirects to login
 */
function handleTokenInvalid(
  tokenError: TokenErrorResponse,
  authCleanupService: AuthCleanupService,
  router: Router
): Observable<never> {
  redirectToLogin(tokenError, authCleanupService, router);
  return throwError(() => new Error(`Token invalid: ${tokenError.code}`));
}

/**
 * Handles generic token errors
 */
function handleGenericTokenError(
  tokenError: TokenErrorResponse,
  authCleanupService: AuthCleanupService,
  router: Router
): Observable<never> {
  redirectToLogin(tokenError, authCleanupService, router);
  return throwError(() => new Error(`Authentication failed: ${tokenError.message}`));
}

/**
 * Redirects the user to the login page and clears tokens
 */
function redirectToLogin(
  tokenError: TokenErrorResponse,
  authCleanupService: AuthCleanupService,
  router: Router
): void {
  // Clear all authentication data using centralized service
  authCleanupService.clearAllAuthData();

  // Redirect to login using Router
  router
    .navigate(['/sign-in'], {
      queryParams: {
        reason: TokenErrorCode.AUTH_FAILED,
        error: tokenError.message,
      },
    })
    .catch((error) => {
      // Fallback: use window.location if Router fails
      window.location.href = '/sign-in';
    });
}

function shouldAttemptRefresh(req: HttpRequest<any>, tokenService: TokenService): boolean {
  // Don't attempt refresh on refresh token endpoint to avoid loops
  if (req.url.includes('refresh-token')) {
    return false;
  }

  // Try refresh if we have a refresh token available
  // Server will validate if the refresh token is valid
  return !!tokenService.getRefreshToken();
}

/**
 * Handles generic token refresh for 401 errors without specific code
 */
function handleGenericTokenRefresh(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  tokenService: TokenService,
  refreshTokenService: RefreshTokenService,
  authCleanupService: AuthCleanupService,
  router: Router
): Observable<HttpEvent<any>> {
  // Capturar el RT antes de iniciar el refresh.
  // Usado en el catchError para detectar si otra pestaña ya refrescó con éxito.
  const sentRefreshToken = tokenService.getRefreshToken();

  return refreshTokenService.refreshToken().pipe(
    switchMap(() => retryRequest(req, next, tokenService)),
    catchError((refreshError) => {
      // Staleness Check: si el RT en localStorage cambió mientras esta pestaña
      // fallaba, significa que otra pestaña ganó la carrera y ya guardó tokens
      // válidos. Reutilizarlos en lugar de forzar el logout.
      const currentRefreshToken = tokenService.getRefreshToken();
      if (currentRefreshToken && currentRefreshToken !== sentRefreshToken) {
        return retryRequest(req, next, tokenService);
      }
      const genericError: TokenErrorResponse = {
        statusCode: 401,
        error: 'Authentication Error',
        message: 'Token refresh failed',
        code: TokenErrorCode.AUTH_FAILED,
      };
      redirectToLogin(genericError, authCleanupService, router);
      return throwError(() => refreshError);
    })
  );
}

function retryRequest(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  tokenService: TokenService
): Observable<HttpEvent<any>> {
  const newToken = tokenService.getAccessToken();
  if (newToken) {
    const newReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${newToken}`,
      },
    });
    return next(newReq);
  }
  return throwError(() => new Error('No access token available after refresh'));
}
